// Finance → Commissions under the compensation plans (CEO 2026-10-15).
// Shown to Finance / admin once GET /commissions/plan-status says ready;
// until then CommissionsPayoutPage keeps today's payout page.
//
// Tabs live in the URL (?tab=orders&order=<id>, ?tab=payouts&batch=<id>) so
// a link from the order page or the Overview lands on the right view.
import { Link, useSearchParams } from "react-router-dom";
import { PageHeader } from "@/components/ui/PageHeader";
import { cn } from "@/lib/utils";
import { OverviewTab } from "./OverviewTab";
import { OrdersTab } from "./OrdersTab";
import { PayoutsTab } from "./PayoutsTab";
import { BonusesTab } from "./BonusesTab";
import { AdjustmentsTab } from "./AdjustmentsTab";
import { PlansRepsTab } from "./PlansRepsTab";
import { SettingsTab } from "./SettingsTab";

export const COMMISSION_TABS = [
  { key: "overview", label: "Overview" },
  { key: "orders", label: "Orders" },
  { key: "payouts", label: "Payouts" },
  { key: "bonuses", label: "Bonuses" },
  { key: "adjustments", label: "Adjustments" },
  { key: "plans", label: "Plans & reps" },
  { key: "settings", label: "Settings" },
] as const;
export type CommissionTab = (typeof COMMISSION_TABS)[number]["key"];

export function CommissionsPlansPage() {
  const [params, setParams] = useSearchParams();
  const raw = params.get("tab");
  const tab: CommissionTab = COMMISSION_TABS.some((t) => t.key === raw) ? (raw as CommissionTab) : "overview";

  /** Switch tab (and optionally set the tab's own params, e.g. a filter). */
  const go = (next: CommissionTab, extra?: Record<string, string>) => {
    const p = new URLSearchParams();
    p.set("tab", next);
    for (const [k, v] of Object.entries(extra ?? {})) p.set(k, v);
    setParams(p);
  };

  return (
    <div className="w-full">
      <PageHeader
        eyebrow="Finance / Commissions"
        title="Commissions"
        intro="Commission on every order under each rep's compensation plan — from estimate to eligible to paid — plus bonuses, adjustments and the biweekly payout."
        action={
          <Link
            to="/finance/commissions/statement"
            className="inline-flex h-11 items-center rounded-md border border-rule bg-white px-4 text-[15px] font-medium text-ink hover:bg-cream-100"
          >
            Rep statements
          </Link>
        }
      />
      <div
        className="mb-6 flex flex-wrap gap-x-7 border-b border-rule"
        role="tablist"
        aria-label="Commissions sections"
        onKeyDown={(e) => {
          // Arrow keys move between tabs (WAI-ARIA tabs pattern).
          if (e.key !== "ArrowRight" && e.key !== "ArrowLeft" && e.key !== "Home" && e.key !== "End") return;
          e.preventDefault();
          const i = COMMISSION_TABS.findIndex((t) => t.key === tab);
          const n = COMMISSION_TABS.length;
          const j = e.key === "Home" ? 0 : e.key === "End" ? n - 1 : (i + (e.key === "ArrowRight" ? 1 : -1) + n) % n;
          const next = COMMISSION_TABS[j]!.key;
          go(next);
          requestAnimationFrame(() => document.getElementById(`comm-tab-${next}`)?.focus());
        }}
      >
        {COMMISSION_TABS.map((t) => (
          <button
            key={t.key}
            id={`comm-tab-${t.key}`}
            type="button"
            role="tab"
            aria-selected={tab === t.key}
            aria-controls="comm-tabpanel"
            tabIndex={tab === t.key ? 0 : -1}
            onClick={() => go(t.key)}
            className={cn(
              "-mb-px min-h-[46px] border-b-2 px-0.5 text-[16px] transition-colors",
              tab === t.key ? "border-ink font-semibold text-ink" : "border-transparent text-muted hover:text-ink",
            )}
          >
            {t.label}
          </button>
        ))}
      </div>
      <div id="comm-tabpanel" role="tabpanel" aria-labelledby={`comm-tab-${tab}`}>
        {tab === "overview" && <OverviewTab go={go} />}
        {tab === "orders" && <OrdersTab />}
        {tab === "payouts" && <PayoutsTab go={go} />}
        {tab === "bonuses" && <BonusesTab go={go} />}
        {tab === "adjustments" && <AdjustmentsTab />}
        {tab === "plans" && <PlansRepsTab />}
        {tab === "settings" && <SettingsTab />}
      </div>
    </div>
  );
}

export type GoToTab = (next: CommissionTab, extra?: Record<string, string>) => void;
